/**
 * Pure derivations shared by the store and screens — ported from the prototype's
 * seatedCount()/billSubOf()/billGst()/billTotal()/orderTotal() (SK Hospitality.dc.html ~1175, 1338-1342).
 */
import type { FloorTableState, Order, OrderStatus } from './seed';
import type { TableStatus } from '../theme/constants';

/**
 * Local OrderStatus from the REAL SK Billing document status — never a local
 * guess. 'bill_sent' means the document has actually been issued (or paid),
 * not just that someone clicked a button; a still-DRAFT document is 'open'
 * regardless of any earlier optimistic local state. Used by both the poll
 * (applyBillingDoc) and reload rehydration (orderFromBillingDoc) so a
 * "sent" bill can't silently revert just because the page refreshed.
 */
export const orderStatusFromDoc = (docStatus?: string): OrderStatus =>
  docStatus === 'ISSUED' || docStatus === 'PARTIALLY_PAID' || docStatus === 'PAID' ? 'bill_sent' : 'open';

export const seatedCount = (tables: FloorTableState[]): number => tables.filter((t) => t.status === 'seated').length;
export const statusCount = (tables: FloorTableState[], status: TableStatus): number => tables.filter((t) => t.status === status).length;

/**
 * Real party size for one table. `party` is a display string ("Priya · 4")
 * written at seat time, not a bare number — parse the trailing size out of
 * it; fall back to the table's seat capacity when unparseable (never a
 * fabricated flat number like the old hardcoded `2`).
 */
export const partySizeOf = (t: FloorTableState): number => {
  const parsed = Number(t.party?.split('·').pop()?.trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : t.seats;
};

/** Real guest headcount currently in the room — sum of partySizeOf across seated tables. */
export const guestsInRoom = (tables: FloorTableState[]): number =>
  tables.filter((t) => t.status === 'seated').reduce((sum, t) => sum + partySizeOf(t), 0);

/**
 * One row per currently-seated table for the Orders & Upsell "Open orders"
 * list. A seated table with no order yet (host seated it, nobody's tapped in
 * to take the order) still shows up here — as a real-looking row sourced
 * from the floor state, not a fabricated billing document — so a manager
 * glancing at this list sees every occupied table, not just the ones a
 * server happened to open first. Tapping a no-order-yet row is what
 * actually creates the real order (via findOrCreateOrderForTable).
 */
export interface OpenOrderRow {
  key: string;
  tableId: string;
  tableLabel: string;
  /** null only for a legacy order merged in from billing with no recoverable
   *  guest count (predates the notes-based metadata fix). */
  guests: number | null;
  server: string;
  status: Order['status'] | 'seated';
  items: number;
  grandTotal: number;
  orderId: string | null;
  hasUpsell: boolean;
  /** True once the table's been freed — this row is history, not a live seat.
   *  Keeps the list from going blank the moment nobody's currently seated or
   *  waiting: the last real bill (sent or settled) stays visible instead of
   *  vanishing along with the table link. */
  finished: boolean;
}

export const openOrderRows = (tables: FloorTableState[], orders: Order[]): OpenOrderRow[] => {
  const seatedRows: OpenOrderRow[] = tables
    .filter((t) => t.status === 'seated')
    .map((t) => {
      const order = t.orderId ? orders.find((o) => o.id === t.orderId) : undefined;
      if (order) {
        const items = order.lines.reduce((a, l) => a + l.qty, 0);
        // A tap that merely navigates into the order builder (findOrCreateOrderForTable)
        // creates this local order record before any item is added — don't let that
        // stray tap flip the row to "Open"; only a real line item earns that label.
        return {
          key: order.id,
          tableId: t.id,
          tableLabel: order.table,
          guests: order.guests,
          server: order.server,
          status: items > 0 ? order.status : ('seated' as const),
          items,
          grandTotal: order.grandTotal ?? 0,
          orderId: order.id,
          hasUpsell: !!order.upsell && !order.accepted && !order.declined,
          finished: false,
        };
      }
      return {
        key: t.id,
        tableId: t.id,
        tableLabel: t.label,
        guests: partySizeOf(t),
        server: t.server,
        status: 'seated' as const,
        items: 0,
        grandTotal: 0,
        orderId: null,
        hasUpsell: false,
        finished: false,
      };
    });

  // Freeing a table (markSettled) only clears the floor-table link — the real
  // order (with its real lines/total) stays in `orders`, just orphaned. Without
  // this, the screen goes completely blank the instant the last seated table
  // frees up, even though a real, just-finished bill exists. Surface the most
  // recent ones as read-only history rows underneath the live ones.
  const seatedOrderIds = new Set(seatedRows.map((r) => r.orderId).filter((id): id is string => id !== null));
  const FINISHED_LIMIT = 15;
  const finishedRows: OpenOrderRow[] = orders
    .filter((o) => !seatedOrderIds.has(o.id) && !o.id.startsWith('ord_'))
    .slice(0, FINISHED_LIMIT)
    .map((o) => ({
      key: o.id,
      tableId: '',
      tableLabel: o.table,
      guests: o.guests,
      server: o.server,
      status: o.status,
      items: o.lines.reduce((a, l) => a + l.qty, 0),
      grandTotal: o.grandTotal ?? 0,
      orderId: o.id,
      hasUpsell: false,
      finished: true,
    }));

  return [...seatedRows, ...finishedRows];
};

export const billSubOf = (o?: Order | null): number => (o ? o.lines.reduce((a, l) => a + l.qty * l.price, 0) : 0);
export const billGstOf = (o?: Order | null): number => billSubOf(o) * 0.05;
export const billTotalOf = (o?: Order | null): number => billSubOf(o) + billGstOf(o);
export const orderTotal = (o?: Order | null): number => billSubOf(o) * 1.05;
